// Password-confirm modal — a re-authentication gate for high-consequence actions
// (deleting a company, and any future "prove it's really you" step). Unlike
// ReasonModal (free-text audit reason), this collects the caller's OWN password in a
// masked field and hands it back to the caller to submit. Esc or click-outside cancels.

import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/primitives/Button';
import { cn } from '@/lib/cn';

export interface PasswordConfirmModalProps {
  open: boolean;
  title: string;
  body?: string;
  /** Extra emphasis line rendered in the danger tone (e.g. what will be deleted). */
  warning?: string;
  confirmLabel?: string;
  tone?: 'danger' | 'warn' | 'primary';
  busy?: boolean;
  /** Server-side error (e.g. wrong password) to show inline. */
  error?: string | null;
  onConfirm: (password: string) => void;
  onCancel: () => void;
}

export function PasswordConfirmModal({
  open,
  title,
  body,
  warning,
  confirmLabel = 'Confirm',
  tone = 'danger',
  busy = false,
  error,
  onConfirm,
  onCancel,
}: PasswordConfirmModalProps) {
  const [password, setPassword] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) { setPassword(''); return; }
    // Focus after paint so the field is ready to type into.
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onCancel(); } };
    document.addEventListener('keydown', onKey, { capture: true });
    return () => { clearTimeout(t); document.removeEventListener('keydown', onKey, { capture: true }); };
  }, [open, onCancel]);

  if (!open) return null;

  const empty = password.length === 0;
  const headTone =
    tone === 'danger' ? 'text-crimson' :
    tone === 'warn'   ? 'text-citrus'   :
                        'text-obsidian';

  function submit() {
    if (!empty && !busy) onConfirm(password);
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-obsidian/40 p-4"
      onClick={() => !busy && onCancel()}
    >
      <div className="w-full max-w-md rounded-lg bg-frost p-5 shadow-hero" onClick={(e) => e.stopPropagation()}>
        <h3 className={cn('font-display text-display-sm font-semibold', headTone)}>{title}</h3>
        {body && <p className="mt-2 text-body-sm text-slate whitespace-pre-line">{body}</p>}
        {warning && (
          <p className="mt-3 rounded-md border border-crimson/30 bg-crimson/[0.05] px-3 py-2 text-body-sm text-crimson">
            {warning}
          </p>
        )}
        <label className="mt-4 block text-micro font-medium uppercase text-cool">Confirm with your password</label>
        <input
          ref={inputRef}
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
          placeholder="Your account password"
          className="mt-1.5 w-full rounded-md border border-mist bg-haze px-3 py-2 text-body-md text-obsidian outline-none focus:border-cobalt/60 focus:shadow-focus"
        />
        {error && <div className="mt-2 text-caption text-crimson">{error}</div>}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button
            variant={tone === 'danger' ? 'danger' : tone === 'warn' ? 'warm' : 'primary'}
            onClick={submit}
            disabled={busy || empty}
            data-testid="password-confirm"
          >
            {busy ? 'Working…' : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
